/**
 * NoteDraftsList — the quiet "Your drafts" affordance (0130, founder
 * ruling A) on the client record's Notes tab.
 *
 * Author-only by construction: the server returns ONLY the caller's own
 * drafts for this client. Deliberately quiet — renders NOTHING when
 * there are no drafts (an empty drafts shelf is noise), and errors
 * render nothing too (drafts are a convenience tier; the feed and the
 * composer are the load-bearing surfaces).
 *
 * Actions per draft:
 *   - Open — hands the draft to the host, which remounts the composer
 *     prefilled; posting from there runs the NORMAL post path and
 *     consumes the draft server-side.
 *   - Discard — two-step inline confirm, then a hard server delete
 *     (drafts never reached the record; nothing references them).
 */

import { type ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import {
  noteDraftsApi,
  noteDraftsQueryKey,
  useNoteDrafts,
  type NoteDraftWire,
} from '@/lib/noteDrafts';
import { Button } from '@/components/primitives/Button';

const PREVIEW_CHARS = 160;

function preview(body: string): string {
  const oneLine = body.replace(/\s+/g, ' ').trim();
  return oneLine.length > PREVIEW_CHARS ? `${oneLine.slice(0, PREVIEW_CHARS)}…` : oneLine;
}

export interface NoteDraftsListProps {
  clientId: string;
  /** Open this draft in the composer (host remounts it prefilled). */
  onOpenDraft: (draft: NoteDraftWire) => void;
}

export function NoteDraftsList({ clientId, onOpenDraft }: NoteDraftsListProps): ReactNode {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  const { drafts } = useNoteDrafts(tenantSlug, clientId);
  const [confirmingDiscard, setConfirmingDiscard] = useState<string | null>(null);

  const discardMut = useMutation({
    mutationFn: (draftId: string) => noteDraftsApi.discard(tenantSlug, draftId),
    onSettled: () => {
      setConfirmingDiscard(null);
      void queryClient.invalidateQueries({ queryKey: noteDraftsQueryKey(tenantSlug, clientId) });
    },
  });

  if (drafts.length === 0) return null;

  return (
    <section
      data-testid="note-drafts-list"
      className="bg-paper rounded-card shadow-sh1 border border-sage-800/[8%] p-5 space-y-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-base text-sage-800">
          Your drafts <span className="text-ink-soft">({drafts.length})</span>
        </h3>
        <span className="text-xs text-ink-soft">
          Only you can see these. Nothing here is on the record yet.
        </span>
      </div>

      <ul className="space-y-2">
        {drafts.map((d) => (
          <li
            key={d.id}
            data-testid={`note-draft-${d.id}`}
            className="rounded-base border border-sage-800/[8%] bg-sand/40 px-3 py-2.5"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-ink-soft">
              <span className="font-semibold text-sage-800">
                {d.kind === 'assessment' ? 'Draft assessment' : 'Draft case note'}
              </span>
              <span>{d.occurred_on}</span>
              <span data-testid={`note-draft-hours-${d.id}`}>
                {d.hours !== null ? `${d.hours} h` : 'Hours not set yet'}
              </span>
              {d.source === 'assessment_drafter' && (
                <span data-testid={`note-draft-source-${d.id}`}>From the Formal Assessment Report</span>
              )}
            </div>
            <p className="mt-1 text-sm text-ink" data-testid={`note-draft-preview-${d.id}`}>
              {preview(d.body)}
            </p>
            <div className="mt-2 flex items-center gap-2">
              {confirmingDiscard === d.id ? (
                <>
                  <span className="text-xs text-ink">Discard this draft? It can't come back.</span>
                  <Button
                    size="sm"
                    data-testid={`note-draft-discard-confirm-${d.id}`}
                    loading={discardMut.isPending}
                    onClick={() => discardMut.mutate(d.id)}
                  >
                    Discard it
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`note-draft-discard-back-${d.id}`}
                    onClick={() => setConfirmingDiscard(null)}
                  >
                    Keep it
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid={`note-draft-open-${d.id}`}
                    onClick={() => onOpenDraft(d)}
                  >
                    Open
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`note-draft-discard-${d.id}`}
                    onClick={() => setConfirmingDiscard(d.id)}
                  >
                    Discard
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
